import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { companyAtom } from "@/store/company";
import { Company, Category } from "@/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Divide, InfoIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import CombustionIcon from "../icons/CombustionIcon";
import ElectricityIcon from "../icons/ElectricityIcon";
import RechargesIcon from "../icons/RechargesIcon";
import VehicleIcon from "../icons/VehicleIcon";

interface MeasureSideMenuProps {
  onCategorySelect?: (category: Category) => void;
  selectedCategoryId?: number;
}

export default function MeasureSideMenu({
  onCategorySelect,
  selectedCategoryId,
}: MeasureSideMenuProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [company, setCompany] = useAtom(companyAtom);
  const [categoriesByScope, setCategoriesByScope] = useState<
    Record<string, Category[]>
  >({});

  // Fetch companies
  const { data: companiesData = { results: [] } } = useQuery<{
    results: Company[];
  }>({
    queryKey: ["companies"],
    queryFn: async () => {
      try {
        const url = getApiUrl("companies/");
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch companies: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching companies:", error);
        return [];
      }
    },
    enabled: !!token,
  });

  // Fetch company categories when company changes
  const { data: companyCategories = [] } = useQuery<any[]>({
    queryKey: ["company-categories", company?.id],
    queryFn: async () => {
      if (!company?.id) return [];

      try {
        const url = getApiUrl(
          `company-categories/by_company/?company_id=${company.id}`,
        );
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch company categories: ${response.status}`,
          );
        }

        const data = await response.json();
        return data.results || data || [];
      } catch (error) {
        console.error("Error fetching company categories:", error);
        return [];
      }
    },
    enabled: !!company?.id,
  });

  // Group categories by scope (level) when they change
  useEffect(() => {
    if (companyCategories.length > 0) {
      const grouped: Record<string, Category[]> = {};

      // Filter only active categories and map them to the correct format
      const activeCategories = companyCategories
        .filter((item) => item.is_active)
        .map((item) => ({
          id: item.id,
          name: item.category_details?.name || "",
          level: item.category_details?.level || "unknown",
          is_active: item.is_active,
          category_details: item.category_details || {},
        }));

      activeCategories.forEach((category) => {
        const level = category.level || "unknown";
        if (!grouped[level]) {
          grouped[level] = [];
        }
        grouped[level].push(category);
      });

      setCategoriesByScope(grouped);

      // Auto-select first category if none is selected
      if (
        !selectedCategoryId &&
        onCategorySelect &&
        activeCategories.length > 0
      ) {
        onCategorySelect(activeCategories[0]);
      }
    } else {
      setCategoriesByScope({});
    }
  }, [companyCategories, onCategorySelect, selectedCategoryId]);

  // Handle company change
  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companiesData?.results?.find(
      (c) => c.id.toString() === companyId,
    );
    if (selectedCompany) {
      setCompany(selectedCompany);
    }
  };

  // Handle category selection
  const handleCategorySelect = (category: Category) => {
    if (onCategorySelect) {
      onCategorySelect(category);
    }
  };

  // Get scope names in Spanish
  const getScopeName = (scope: string) => {
    switch (scope.toLowerCase()) {
      case "1":
        return "1";
      case "2":
        return "2";
      case "3":
        return "3";
      default:
        return scope;
    }
  };

  const getCategoryIcon = (category: Category) => {
    console.log("category", category);
    switch (category.name.toLowerCase()) {
      case "electricity":
        return <ElectricityIcon />;
      case "combustion":
        return <CombustionIcon />;
      case "recharges":
        return <RechargesIcon />;
      case "vehicles":
        return <VehicleIcon />;
      default:
        return undefined;
    }
  };

  return (
    <div className="bg-[#f5f5f5] border-r h-full py-4 px-2 w-[189px] animate-slide-in-left-1/2s">
      {/* Company Selector */}
      <div className="mb-6">
        {/* <label className="text-2xl font-semibold">{t("companies.title")}</label> */}
        <Select
          value={company?.id?.toString()}
          onValueChange={handleCompanyChange}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("companies.select_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {companiesData?.results?.map((company) => (
              <SelectItem key={company.id} value={company.id.toString()}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Categories by Scope */}
      {company ? (
        <div className="space-y-4">
          {Object.keys(categoriesByScope)
            .sort()
            .map((scope) => (
              <div key={scope} className="space-y-2">
                <h3 className="font-black text-sm text-black py-2">
                  {`${t("common.level")} ${getScopeName(scope)}`}
                </h3>
                <div className={`space-y-1 `}>
                  {categoriesByScope[scope].map((category) => (
                    <div
                      key={category.id}
                      className={`w-full flex items-center space-x-2  ${selectedCategoryId === category.id ? "bg-accent" : "bg-muted/50 hover:bg-[#e5e5e5]"}`}
                    >
                      <span>{getCategoryIcon(category)}</span>
                      <div
                        role="button"
                        key={category.id}
                        variant="ghost"
                        className={`w-full justify-start text-sm capitalize py-2 px-2`}
                        onClick={() => handleCategorySelect(category)}
                      >
                        <span className="font-normal">{t(category.name)}</span>
                      </div>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="hover:bg-[#e5e5e5]"
                          >
                            <InfoIcon className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent>
                          {/* <p>{t(`info.${category.name.toLowerCase()}`)}</p> */}
                          <div
                            dangerouslySetInnerHTML={{
                              __html: t(`info.${category.name.toLowerCase()}`),
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  ))}
                </div>
              </div>
            ))}

          {/* Add Category Button */}
          {/* <Button variant="outline" size="sm" className="w-full mt-4 text-xs">
            <Plus className="h-4 w-4 mr-1" />
            {t("category.add")}
          </Button> */}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-4">
          {t("companies.select_placeholder")}
        </div>
      )}
    </div>
  );
}
